import { useMemo } from "react";
import useSWR from "swr";
import { AgentAvatarInput } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { Agent } from "~/services/agents";
import { getSharedConversation } from "~/services/shares";

// SWR key prefix for a featured entry's live share read. Exported so the run-end
// revalidation (`useProjectSessionsSync`) can target these reads by prefix
// without duplicating the literal — keeping the two in lockstep.
export const FEATURED_SESSION_SHARE_KEY = "featured-session-share";

// A featured entry's served set is resolved live from its share — the
// serving-agent summaries on the shared-conversation read — so an agent that
// joined the session after it was pinned still appears. SWR's own cache dedupes
// entries that happen to share a token. Only when the share can't be read
// (revoked, or not permitted to the viewer) does the row fall back to the
// entry's stored `agent_id`, already resolved by the caller (`useProjectAgentsById`).
export const useFeaturedSessionServedAgents = (
  shareToken: string,
  fallbackAgent: Agent | undefined
): AgentAvatarInput[] => {
  const { data: share } = useSWR([FEATURED_SESSION_SHARE_KEY, shareToken], () =>
    getSharedConversation(shareToken)
  );

  return useMemo(() => {
    if (share) {
      return share.agents;
    }
    return fallbackAgent ? [fallbackAgent] : [];
  }, [share, fallbackAgent]);
};
